import React from 'react';
import { ProductApi } from 'common/types';
import imagePlaceholder from 'common/assets/image-placeholder.svg';

import {
	ListItem,
	ListItemText,
	ListItemAvatar,
	ListItemSecondaryAction,
	IconButton,
	Avatar,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useTranslation } from 'services/localization/useTranslation';
import classNames from 'classnames';
import { RiArrowUpLine, RiArrowDownLine, RiCloseLine } from 'react-icons/ri';
import { useFlash } from 'hooks/useFlash';
import { getProductImageSrc } from 'common/utils/productUtils';

interface Props {
	product: ProductApi;
	onClick?: (product: ProductApi) => void;
	onDelete?: (product: ProductApi) => void;
	onReorder?: (product: ProductApi, direction: 'up' | 'down') => void;
	disabled?: boolean;
}

const ProductListItem = (props: Props) => {
	const { product, onClick, onDelete, onReorder } = props;
	const classes = useStyles();
	const { getTranslation } = useTranslation();
	const productName = getTranslation(product.name);

	const hasActions = !!onDelete || !!onReorder;

	const [isFlashing, flash] = useFlash();

	const handleReorder = (direction: 'up' | 'down') => {
		if (!onReorder) return;

		onReorder(product, direction);
		flash();
	};

	const content = (
		<>
			<ListItemAvatar>
				<Avatar
					variant="rounded"
					alt={productName}
					src={getProductImageSrc(product) || imagePlaceholder}
					classes={{
						img: classNames(classes.img, {
							[classes.imgDisabled]: props.disabled,
						}),
					}}
				/>
			</ListItemAvatar>
			<ListItemText
				primary={productName}
				primaryTypographyProps={{
					color: props.disabled ? 'textSecondary' : 'textPrimary',
					className: classes.textEllipsis,
					sx: {
						paddingRight: hasActions ? '160px' : undefined,
					},
				}}
			/>
			<ListItemSecondaryAction>
				{!!onReorder && (
					<>
						<IconButton
							onClick={() => handleReorder('up')}
							disabled={props.disabled}
							size="large"
							disableRipple
						>
							<RiArrowUpLine />
						</IconButton>
						<IconButton
							onClick={() => handleReorder('down')}
							disabled={props.disabled}
							size="large"
							disableRipple
						>
							<RiArrowDownLine />
						</IconButton>
					</>
				)}
				{!!onDelete && (
					<IconButton onClick={() => onDelete(product)} disabled={props.disabled} size="large">
						<RiCloseLine />
					</IconButton>
				)}
			</ListItemSecondaryAction>
		</>
	);

	return !!onClick ? (
		<ListItem
			divider
			button
			onClick={() => onClick(product)}
			className={classNames(classes.row, { [classes.rowFlashing]: isFlashing })}
		>
			{content}
		</ListItem>
	) : (
		<ListItem divider className={classNames(classes.row, { [classes.rowFlashing]: isFlashing })}>
			{content}
		</ListItem>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		img: {
			objectFit: 'contain',
		},
		imgDisabled: {
			filter: 'grayscale(100%)',
		},
		row: {
			transition: 'background 0.3s',
		},
		rowFlashing: {
			background: theme.palette.background.secondary,
		},
		textEllipsis: {
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
	}),
);

export default ProductListItem;
